/* pure helpers for the Overview session drill-down. */
import { MONTH_SHORT, fmt, num } from '../format';
import { SESSION_STATUS_UNKNOWN } from './params';

// A session row with no recorded status (legacy/frozen NULL) is the UNKNOWN bucket; month-level rows have none.
export const status_bucket = (row) => {
  if (!row) return '';
  if (row.session_status) return row.session_status;
  return row.session_uuid ? SESSION_STATUS_UNKNOWN : '';
};

export const session_status_label = (status) =>
  status === SESSION_STATUS_UNKNOWN ? 'Unknown (not recorded)' : status;

// Filter options from BOTH item and penalty rows (each carries its own status), sorted, UNKNOWN last.
// Server-provided values only — we never invent a status the data doesn't contain.
export const collect_session_statuses = (days) => {
  const seen = [];
  (Array.isArray(days) ? days : []).forEach((d) => {
    (d && Array.isArray(d.rows) ? d.rows : []).forEach((r) => {
      const s = status_bucket(r);
      if (s && seen.indexOf(s) === -1) seen.push(s);
    });
  });
  const known = seen.filter((s) => s !== SESSION_STATUS_UNKNOWN).sort();
  return seen.indexOf(SESSION_STATUS_UNKNOWN) === -1 ? known : [...known, SESSION_STATUS_UNKNOWN];
};

export const session_status_options = (statuses) =>
  (Array.isArray(statuses) ? statuses : []).map((s) => ({
    value: s,
    label: session_status_label(s)
  }));

// "Why this amount?" — renders the PERSISTED explain JSON verbatim (item/penalty detail and the
// month's monthly_explain lines: paycalc.Explain {rule_ref, formula, inputs:[{key,value}]}).
// Nothing is recomputed client-side: the popover's whole job is to show what the engine stored.
export const explain_lines = (detail) => {
  if (!detail) return [];
  let parsed = detail;
  if (typeof detail === 'string') {
    try {
      parsed = JSON.parse(detail);
    } catch (e) {
      // Not JSON — show the stored string as-is rather than dropping it.
      return [{ label: 'Explanation', value: detail }];
    }
  }
  if (!parsed || typeof parsed !== 'object') return [];
  const known = [
    ['rule_ref', 'Rule'],
    ['rule', 'Rule'],
    ['prd_ref', 'PRD reference'],
    ['formula', 'Formula']
  ];
  const lines = [];
  known.forEach(([key, label]) => {
    if (parsed[key] !== null && parsed[key] !== undefined && parsed[key] !== '') {
      lines.push({ label, value: String(parsed[key]) });
    }
  });
  const { inputs } = parsed;
  if (Array.isArray(inputs)) {
    // paycalc.ExplainInput pairs, in the engine's order.
    inputs.forEach((p) => {
      if (!p || typeof p !== 'object') return;
      lines.push({ label: String(p.key), value: String(p.value), is_input: true });
    });
  } else if (inputs && typeof inputs === 'object') {
    Object.keys(inputs).forEach((k) => {
      const v = inputs[k];
      lines.push({
        label: k,
        value: v !== null && typeof v === 'object' ? JSON.stringify(v) : String(v),
        is_input: true
      });
    });
  }
  // Anything the engine stored that we don't have a label for still gets shown — a silently
  // dropped explain key would make the popover lie about what was persisted.
  Object.keys(parsed).forEach((k) => {
    if (k === 'inputs' || known.some(([key]) => key === k)) return;
    const v = parsed[k];
    if (v === null || v === undefined || v === '') return;
    lines.push({ label: k, value: typeof v === 'object' ? JSON.stringify(v) : String(v) });
  });
  return lines;
};

export const has_explain = (detail) => explain_lines(detail).length > 0;

// where the portaled "why" popover sits (position: fixed, viewport coords). Below the amount,
// right-aligned to it and clamped inside the viewport; flipped above when it would run off the
// bottom and there is room above.
export const popover_position = (
  rect,
  pop_height,
  viewport_w,
  viewport_h,
  width = 320,
  gap = 4
) => {
  const below = rect.bottom + gap;
  const above = rect.top - gap - pop_height;
  const flip = pop_height > 0 && below + pop_height > viewport_h && above >= 0;
  const left = Math.max(8, Math.min(rect.right - width, viewport_w - width - 8));
  return { top: flip ? above : below, left, placement: flip ? 'top' : 'bottom' };
};

// Penalties are sent positive and read as deductions ("−₹150.00"); zero stays unsigned.
export const penalty_text = (v) => fmt(num(v) === 0 ? 0 : -num(v), 2);

// '2026-06-12 10:00' (IST) -> '12 Jun 2026, 10:00 IST', the mock's list format; anything else as-is.
export const ist_time_text = (v) => {
  const m = /^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2})/.exec(v || '');
  const mon = m ? MONTH_SHORT[Number(m[2]) - 1] : '';
  return mon ? `${Number(m[3])} ${mon} ${m[1]}, ${m[4]}:${m[5]} IST` : v || '—';
};

// Only the selected teacher's lines; a row without teacher_uuid (today's API) is already scoped by the server.
export const own_rows = (rows, teacher_uuid) =>
  (Array.isArray(rows) ? rows : []).filter(
    (r) => !r.teacher_uuid || !teacher_uuid || r.teacher_uuid === teacher_uuid
  );

const sum_or_null = (rows, key) => {
  const vals = rows.map((r) => r[key]).filter((v) => v !== null && v !== undefined);
  return vals.length ? Math.round(vals.reduce((a, v) => a + Number(v), 0) * 100) / 100 : null;
};

/*
 * One row per session (the pop-up is one teacher's): a session's pay line and its penalties, e.g. a
 * REPLACED_OUT ₹0 line plus the replacement penalty, read as one row. Amounts are summed (totals
 * unchanged), each line kept in `parts` and each penalty in `penalties`. Month-level rows (no session)
 * and single-line sessions pass through as they are.
 */
export const group_session_rows = (rows) => {
  const order = [];
  const by_session = {};
  (Array.isArray(rows) ? rows : []).forEach((r) => {
    if (!r.session_uuid) {
      order.push({ single: r });
      return;
    }
    if (!by_session[r.session_uuid]) {
      by_session[r.session_uuid] = [];
      order.push({ session_uuid: r.session_uuid });
    }
    by_session[r.session_uuid].push(r);
  });
  return order.map((o) => {
    if (o.single) return o.single;
    const parts = by_session[o.session_uuid];
    if (parts.length === 1) return parts[0];
    // the pay line leads (its status, duration and explanation); penalties follow in server order
    const items = parts.filter((p) => p.kind !== 'penalty');
    const penalties = parts.filter((p) => p.kind === 'penalty');
    const lead = items[0] || parts[0];
    const first = (key) => (parts.find((p) => p[key] && p[key] !== '—') || lead)[key];
    return {
      ...lead,
      key: `session-${o.session_uuid}`,
      kind: 'session',
      session_label: first('session_label'),
      session_kind: first('session_kind'),
      duration_label: first('duration_label'),
      // sortable text; the cell lists each part's own type
      type_label: parts.map((p) => p.type_label).join(' · '),
      payment: sum_or_null(parts, 'payment'),
      incentive: sum_or_null(parts, 'incentive'),
      penalty: sum_or_null(penalties, 'penalty'),
      parts: [...items, ...penalties],
      penalties
    };
  });
};
